import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Receipt, Sparkles, Crown } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";

const PURPOSE_LABEL = {
  topup: "Credit top-up",
  payperuse: "Pay-per-use pack",
  subscription: "Pro subscription",
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [refreshed, setRefreshed] = useState(false);

  const purpose = params.get("purpose") || "topup";
  const creditsAdded = Number(params.get("credits") || 0);
  const paymentId = params.get("pid") || "";
  const amount = params.get("amount") || "";

  // Refresh user once on mount so the navbar/credits show the new balance
  useEffect(() => {
    (async () => {
      if (refresh) await refresh();
      setRefreshed(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If a user somehow lands here without params (e.g. direct visit), bounce home
  useEffect(() => {
    if (!params.get("purpose") && !params.get("credits")) {
      const t = setTimeout(() => navigate("/dashboard", { replace: true }), 100);
      return () => clearTimeout(t);
    }
  }, [params, navigate]);

  const isSubscription = purpose === "subscription";

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-black">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-20" data-testid="payment-success-page">
        {/* Celebration hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#FF4500]/10 mb-6">
            <CheckCircle2 size={44} className="text-[#FF4500]" strokeWidth={2.2} />
          </div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
            Payment confirmed
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter leading-[1.05] mb-4">
            {isSubscription ? (
              <>You're <span className="text-[#FF4500]">Pro</span> now.</>
            ) : (
              <>Your credits are <span className="text-[#FF4500]">live</span>.</>
            )}
          </h1>
          <p className="text-zinc-600 text-base max-w-lg mx-auto">
            {isSubscription
              ? "Welcome to Jingle Hooks Pro. 450 credits dropped into your account — go make ads worth scrolling for."
              : "Your account has been topped up. Time to write a hook that won't be skipped."}
          </p>
        </div>

        {/* Receipt card */}
        <div
          className="border border-black/10 bg-white p-6 sm:p-8 mb-8"
          data-testid="payment-success-receipt"
        >
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-black/10">
            <div className="flex items-center gap-2">
              <Receipt size={18} className="text-zinc-500" />
              <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                Order receipt
              </div>
            </div>
            {isSubscription && (
              <div className="flex items-center gap-1.5 bg-black text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">
                <Crown size={11} /> Pro
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-8">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                Purchased
              </div>
              <div className="text-base font-semibold">
                {PURPOSE_LABEL[purpose] || "Payment"}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                Credits added
              </div>
              <div className="font-display text-3xl font-semibold tracking-tighter tabular-nums text-[#FF4500]">
                +{creditsAdded}
              </div>
            </div>
            {amount && (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                  Amount paid
                </div>
                <div className="text-base font-semibold tabular-nums">₹{amount}</div>
              </div>
            )}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                New balance
              </div>
              <div className="text-base font-semibold tabular-nums">
                {refreshed && user ? `${user.credits} credits` : "Updating…"}
              </div>
            </div>
            {paymentId && (
              <div className="sm:col-span-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                  Razorpay payment ID
                </div>
                <div className="font-mono text-xs text-zinc-700 break-all">{paymentId}</div>
              </div>
            )}
          </div>
        </div>

        {/* What's next */}
        <div className="mb-8">
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">
            What's next
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to="/generate"
              className="group border border-black/10 bg-white p-5 hover:border-black hover:shadow-[4px_4px_0_0_#000] transition-all"
              data-testid="success-cta-generate"
            >
              <Sparkles size={20} className="text-[#FF4500] mb-3" />
              <div className="font-display text-xl font-semibold tracking-tight mb-1">
                Start generating
              </div>
              <div className="text-sm text-zinc-600 mb-3">
                Spend your credits on a 15s / 20s / 30s jingle right now.
              </div>
              <div className="flex items-center gap-1.5 text-sm font-semibold text-[#FF4500]">
                Open generator <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
            <Link
              to="/credits"
              className="group border border-black/10 bg-white p-5 hover:border-black hover:shadow-[4px_4px_0_0_#000] transition-all"
              data-testid="success-cta-credits"
            >
              <Receipt size={20} className="text-zinc-700 mb-3" />
              <div className="font-display text-xl font-semibold tracking-tight mb-1">
                View receipt
              </div>
              <div className="text-sm text-zinc-600 mb-3">
                See your full transaction ledger and download invoices.
              </div>
              <div className="flex items-center gap-1.5 text-sm font-semibold text-black">
                Credit history <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </div>

        {/* Secondary CTA */}
        <div className="text-center pt-6 border-t border-black/10">
          <Button
            asChild
            variant="ghost"
            className="text-zinc-600 hover:text-black font-semibold"
            data-testid="success-cta-dashboard"
          >
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </div>

        {/* Reassurance footer */}
        <div className="mt-10 text-center font-mono text-[10px] uppercase tracking-widest text-zinc-400">
          A copy of this receipt has been logged to your credit history · Secured by Razorpay
        </div>
      </main>
    </div>
  );
}
